import { localStore } from '../../v2/js/utils/storage';

const STORAGE_KEY = 'sourcedRegistrationHandoff';

export interface SourcedRegistrationHandoff {
  /** The invited address. Guards against applying these details to another account. */
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  business_name?: string;
  text_opt_in: boolean;
  /** Where the invite wanted the user to land once registration completes. */
  next?: string | null;
}

/**
 * Carries the details an invited 1099 provider typed on the registration page
 * across the Google OAuth round trip.
 *
 * Without this, choosing "Continue with Google" discards everything already
 * entered and drops the user on a second form. Stashing it lets the completion
 * step finish the registration for them, so Google actually *starts* completion
 * rather than restarting it.
 *
 * Deliberately not `sessionContinueUrl`: that key is global to the browser and
 * identity-agnostic, so a stale value gets applied to whoever ends up signed in.
 * These details carry the email they belong to and are only ever read back for
 * that account.
 */
export const writeSourcedRegistrationHandoff = (handoff: SourcedRegistrationHandoff): void => {
  localStore.set(STORAGE_KEY, JSON.stringify(handoff));
};

/**
 * The stashed details, but only when they belong to `email`. Any mismatch —
 * including signing in as a different account than the one invited — yields
 * null, so another user's registration can never be auto-completed.
 */
export const readSourcedRegistrationHandoff = (
  email?: string | null
): SourcedRegistrationHandoff | null => {
  const raw = localStore.get(STORAGE_KEY);
  if (!raw || !email) {
    return null;
  }
  try {
    const handoff = JSON.parse(raw) as SourcedRegistrationHandoff;
    if (handoff?.email?.toLowerCase() !== email.toLowerCase()) {
      return null;
    }
    return handoff;
  } catch {
    return null;
  }
};

export const clearSourcedRegistrationHandoff = (): void => {
  localStore.remove(STORAGE_KEY);
};
